/* RESPONSIBLE TEAM: team-ai-insights */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type PALETTE from '@intercom/pulse/lib/palette';
import { type SerializedChartSeries } from 'embercom/components/reporting/in-context/static-report';
import type { ColorString } from 'highcharts';

type PaletteColor = keyof typeof PALETTE;
// these colors are copies from apps/teammate-app/src/index.css
// we need to use the values directly here so we can pass them to Highcharts
const colorForNoMetricValue: ColorString = '#eff0eb';
const palette: Record<string, PaletteColor[]> = {
  blue: [
    'vis-blue-20',
    'vis-blue-30',
    'vis-blue-40',
    'vis-blue-50',
    'vis-blue-60',
    'vis-blue-70',
    'vis-blue-80',
  ],
  salmon: [
    'vis-salmon-20',
    'vis-salmon-30',
    'vis-salmon-40',
    'vis-salmon-50',
    'vis-salmon-60',
    'vis-salmon-70',
    'vis-salmon-80',
  ],
  slateblue: [
    'vis-slateblue-20',
    'vis-slateblue-30',
    'vis-slateblue-40',
    'vis-slateblue-50',
    'vis-slateblue-60',
    'vis-slateblue-70',
    'vis-slateblue-80',
  ],
  cyan: [
    'vis-cyan-20',
    'vis-cyan-30',
    'vis-cyan-40',
    'vis-cyan-50',
    'vis-cyan-60',
    'vis-cyan-70',
    'vis-cyan-80',
  ],
  violet: [
    'vis-violet-20',
    'vis-violet-30',
    'vis-violet-40',
    'vis-violet-50',
    'vis-violet-60',
    'vis-violet-70',
    'vis-violet-80',
  ],
  mint: [
    'vis-mint-20',
    'vis-mint-30',
    'vis-mint-40',
    'vis-mint-50',
    'vis-mint-60',
    'vis-mint-70',
    'vis-mint-80',
  ],
  pink: [
    'vis-pink-20',
    'vis-pink-30',
    'vis-pink-40',
    'vis-pink-50',
    'vis-pink-60',
    'vis-pink-70',
    'vis-pink-80',
  ],
};

function buildStops(palette: PaletteColor[]): Array<[number, PaletteColor]> {
  return palette.map((color, index) => [index / (palette.length - 1), color]);
}

type MetricConfig = {
  palette: PaletteColor[];
  colorAxis: { stops: Array<[number, PaletteColor]> };
  seriesConfig: SerializedChartSeries;
  metricPickerItemClass: string;
  label: string;
};

type MetricId = keyof typeof metricConfigs;

const metricConfigs: Record<string, MetricConfig> = {
  'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at': {
    palette: palette.violet,
    colorAxis: {
      stops: buildStops(palette.violet),
    },
    metricPickerItemClass: 'bg-vis-violet-50',
    seriesConfig: {
      metric_id:
        'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
    },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'CX Score',
  },
  'fin.participation_rate': {
    palette: palette.blue,
    colorAxis: {
      stops: buildStops(palette.blue),
    },
    metricPickerItemClass: 'bg-vis-blue-50',
    seriesConfig: { metric_id: 'fin.participation_rate' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'Involvement rate',
  },
  'fin.resolution_rate': {
    palette: palette.mint,
    colorAxis: {
      stops: buildStops(palette.mint),
    },
    metricPickerItemClass: 'bg-vis-mint-50',
    seriesConfig: { metric_id: 'fin.resolution_rate' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'Resolution rate',
  },
  'v1.handling_time': {
    palette: palette.salmon,
    colorAxis: {
      stops: buildStops(palette.salmon.reverse()),
    },
    metricPickerItemClass: 'bg-vis-salmon-50',
    seriesConfig: {
      metric_id: 'v1.handling_time',
      aggregation: 'median',
      filters: {
        type: 'exists',
        data: {
          property: 'first_reply_teammate_id',
          attribute: 'conversation.first_reply_teammate_id',
        },
      },
    },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'Handling time',
  },
  'v2.first_response_time.bot_inbox_included': {
    palette: palette.pink,
    colorAxis: {
      stops: buildStops(palette.pink.reverse()),
    },
    metricPickerItemClass: 'bg-vis-pink-50',
    seriesConfig: { metric_id: 'v2.first_response_time.bot_inbox_included', aggregation: 'median' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'First response time',
  },
} as const;

function addTopicFilter(seriesConfig: SerializedChartSeries, topicId: string) {
  let topicFilter = {
    type: 'category',
    data: {
      attribute: 'conversation_custom_fields#ai_insights_topic',
      property: 'conversation_custom_fields#ai_insights_topic',
      values: [topicId],
    },
  };

  return {
    ...seriesConfig,
    filters: {
      type: 'and',
      filters: [topicFilter, seriesConfig.filters].compact(),
    },
  };
}

// Usage: getComputedCssVar('--neutral-fill')
function getComputedCssVar(varName: string) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

export {
  colorForNoMetricValue,
  palette,
  buildStops,
  metricConfigs,
  type MetricId,
  addTopicFilter,
  getComputedCssVar,
};
